import { format } from '@if/core/date'
import { formatCurrencyNoDecimals, formatPercentage } from '@if/core/utils'
import {
  ActionButton,
  BodyText,
  Caption,
  DataField,
  DisplayValue,
  EnumChip,
  ErrorHandler,
  Eyebrow,
  FieldSectionTitle,
  Icon,
  StatCard,
  SubsectionTitle,
  useConfirmDialog,
} from '@if/mui'
import { Box, Card, CardActions, CardContent, CardHeader, Divider, Stack } from '@mui/material'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import { useDeleteCustomBuydownPlan, useTerminateCustomBuydownPlan } from '@src/data/api/custom-buydown-api/custom-buydown-api'
import { useBorrowerContext } from '@src/hooks/borrower-context-hooks'
import { CustomBuydownPlan } from '@src/types/CustomBuydown'
import { Calendar, CircleStop, Lock, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getPromoPlanErrorCode, PromoPlanErrorCode } from '../promoPlanErrorCodes'
import { CustomBuydownPlanStatus, ECustomBuydownPlanStatus } from '../planStatus'
import { useRescheduleUpcomingPlanDialog } from './RescheduleUpcomingPlanDialog'
import { useSetActivePlanEndDateDialog } from './SetActivePlanEndDateDialog'

const planNotFoundError =
  (plan: CustomBuydownPlan): ErrorHandler =>
  (_descriptor, t) => ({
    role: 'contextual',
    message: t('customBuydown.errors.planNotFound', { planCode: plan.label }),
  })

const TERM_TILE_MIN_WIDTH = 92

export interface PlanCardProps {
  plan: CustomBuydownPlan
  status: CustomBuydownPlanStatus
  locked: boolean
  siblingPlan?: CustomBuydownPlan | null
}

export const PlanCard = ({ plan, status, locked, siblingPlan }: PlanCardProps) => {
  const { t } = useTranslation()
  const merchant = useMerchant()
  const context = useBorrowerContext()
  const isScheduled = status === ECustomBuydownPlanStatus.Scheduled
  const maxFinancingAmount = plan.promoTermRates[0]?.maxFinancingAmount

  const setEndDateDialog = useSetActivePlanEndDateDialog(merchant.id, siblingPlan)
  const rescheduleDialog = useRescheduleUpcomingPlanDialog(merchant.id, siblingPlan)

  const deletePlan = useDeleteCustomBuydownPlan(merchant.id)
  const confirmDelete = useConfirmDialog<CustomBuydownPlan>({
    title: t('customBuydown.timeline.deleteConfirmTitle'),
    message: (ctx) =>
      t('customBuydown.timeline.deleteConfirmMessage', {
        planCode: ctx.props.label,
        activeFrom: format(ctx.props.activeFrom, 'P'),
      }),
    mode: 'destructive',
    onError: {
      notFound: planNotFoundError(plan),
      unknown: (descriptor, t) =>
        getPromoPlanErrorCode(descriptor) === PromoPlanErrorCode.PlanAlreadyApplicable
          ? { role: 'contextual', message: t('customBuydown.errors.planAlreadyApplicable', { planCode: plan.label }) }
          : undefined,
    },
    onConfirm: (ctx) => deletePlan.mutateAsync(ctx.props.id),
  })

  const terminatePlan = useTerminateCustomBuydownPlan(merchant.id)
  const confirmTerminate = useConfirmDialog<CustomBuydownPlan>({
    title: (ctx) => t('customBuydown.timeline.terminateConfirmTitle', { planCode: ctx.props.label }),
    message: t('customBuydown.timeline.terminateConfirmMessage'),
    mode: 'destructive',
    confirmLabel: t('customBuydown.timeline.terminateConfirmButton'),
    onError: {
      notFound: planNotFoundError(plan),
      unknown: (descriptor, t) => {
        const code = getPromoPlanErrorCode(descriptor)
        if (code === PromoPlanErrorCode.PlanNotYetActive)
          return { role: 'contextual', message: t('customBuydown.errors.planNotYetActive', { planCode: plan.label }) }
        if (code === PromoPlanErrorCode.PlanAlreadyEnded)
          return { role: 'contextual', message: t('customBuydown.errors.planAlreadyEnded', { planCode: plan.label }) }
        return undefined
      },
    },
    onConfirm: (ctx) => terminatePlan.mutateAsync(ctx.props.id),
  })

  const summary = t(
    maxFinancingAmount && maxFinancingAmount > 0
      ? `customBuydown.timeline.${isScheduled ? 'summaryScheduled' : 'summaryActive'}`
      : `customBuydown.timeline.${isScheduled ? 'summaryScheduledNoCap' : 'summaryActiveNoCap'}`,
    {
      adminFee: `${formatPercentage(plan.borrowerFeeRate * 100)}%`,
      cap: maxFinancingAmount ? formatCurrencyNoDecimals(maxFinancingAmount) : '',
    },
  )

  return (
    <Card>
      <CardHeader
        disableTypography
        title={
          <Stack spacing={0.75}>
            <Eyebrow>
              {isScheduled
                ? t('customBuydown.timeline.scheduledOverline', { date: format(plan.activeFrom, 'P') })
                : t('customBuydown.timeline.activeOverline')}
            </Eyebrow>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <SubsectionTitle>{plan.label}</SubsectionTitle>
              <EnumChip set={ECustomBuydownPlanStatus} value={status} size="small" />
            </Stack>
          </Stack>
        }
        action={<DisplayValue color="primary">{formatPercentage(plan.merchantFeeRate * 100)}%</DisplayValue>}
        sx={{ alignItems: 'center', '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' } }}
      />
      <CardContent>
        <Stack spacing={2.5}>
          <BodyText>{summary}</BodyText>
          <Divider />
          <Stack direction="row" spacing={4}>
            <DataField
              label={t(`customBuydown.timeline.${isScheduled ? 'starts' : 'started'}`)}
              value={format(plan.activeFrom, 'P')}
            />
            <DataField
              label={t('customBuydown.timeline.ends')}
              value={plan.expiresOn ? format(plan.expiresOn, 'P') : t('customBuydown.timeline.noEndDate')}
            />
          </Stack>
          <Stack spacing={1}>
            <FieldSectionTitle>{t('customBuydown.timeline.ratesTitle', { context })}</FieldSectionTitle>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(${TERM_TILE_MIN_WIDTH}px, 1fr))`,
                gap: 1.5,
              }}
            >
              {plan.promoTermRates.map((termRate) => (
                <StatCard
                  key={termRate.termMonths}
                  size="sm"
                  label={t('customBuydown.termLabel', { months: termRate.termMonths })}
                  value={`${formatPercentage(termRate.promoRate * 100)}%`}
                />
              ))}
            </Box>
          </Stack>
        </Stack>
      </CardContent>
      {locked && (
        <CardActions>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Icon icon={Lock} size="xs" color="disabled" />
            <Caption>{t('customBuydown.inherited.setByBanner')}</Caption>
          </Stack>
        </CardActions>
      )}
      {!locked && isScheduled && (
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <ActionButton size="small" startIcon={Calendar} onClick={() => void rescheduleDialog.edit(plan)}>
            {t('customBuydown.timeline.changeDatesAction')}
          </ActionButton>
          <ActionButton tone="destructive" size="small" startIcon={Trash2} onClick={() => void confirmDelete.open(plan)}>
            {t('customBuydown.timeline.deleteAction')}
          </ActionButton>
        </CardActions>
      )}
      {!locked && !isScheduled && (
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <ActionButton size="small" startIcon={Calendar} onClick={() => void setEndDateDialog.edit(plan)}>
            {t('customBuydown.timeline.setEndDateAction')}
          </ActionButton>
          <ActionButton tone="destructive" size="small" startIcon={CircleStop} onClick={() => void confirmTerminate.open(plan)}>
            {t('customBuydown.timeline.terminateAction')}
          </ActionButton>
        </CardActions>
      )}
    </Card>
  )
}
